import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  ArrowRight, ArrowUp, ArrowDown, Minus,
  Check, CheckCircle, ChevronRight, ChevronDown,
  CreditCard, Loader2,
  AlertTriangle, Shield, BarChart3,
  UserCheck, Clock, Lock,
  History, FlaskConical, ListTodo,
  Play, Calendar,
  Save,
} from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;

/* ════ BAND STYLE TOKENS — Executive Clarity ════ */
const BAND = {
  green:  { dot: "#10b981", text: "#059669", bg: "#ecfdf5", border: "#a7f3d0", label: "Healthy" },
  yellow: { dot: "#f59e0b", text: "#b45309", bg: "#fffbeb", border: "#fde68a", label: "Caution" },
  red:    { dot: "#ef4444", text: "#b91c1c", bg: "#fef2f2", border: "#fecaca", label: "High Risk" },
};
const bandOf = (b) => BAND[b] || BAND.red;

const api = (path, opts) => fetch(`${B}/api/cdu/${path}`, opts).then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); });
const govApi = (path, opts) => fetch(`${B}/api/cdu/gov/${path}`, opts).then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); });
const post = (path, body) => govApi(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
const patch = (path, body) => govApi(path, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

/* ════ 0-4 SCALE ════ */
const SCALES = [
  { value: 0, label: "Absent" },
  { value: 1, label: "Weak" },
  { value: 2, label: "Developing" },
  { value: 3, label: "Strong" },
  { value: 4, label: "Exemplary" },
];

const ScaleInput = ({ value, onChange, qid, size = "md" }) => {
  const dim = size === "sm" ? "h-8 w-9 text-[12px]" : "h-10 w-11 text-[13px]";
  return (
    <div className="flex items-center gap-1.5">
      {SCALES.map(o => {
        const active = value === o.value;
        return (
          <button
            key={o.value} type="button" onClick={() => onChange(o.value)}
            className={`${dim} rounded-[8px] font-semibold border transition-all ${active
              ? "bg-[#1a56db] text-white border-[#1a56db] shadow-[0_0_0_3px_rgba(26,86,219,0.12)]"
              : "bg-[#f9fafb] text-[#4b5563] border-[#e5e7eb] hover:bg-[#eff6ff] hover:border-[#bfdbfe] hover:text-[#1a56db]"}`}
            data-testid={`scale-${qid}-${o.value}`}
          >{o.value}</button>
        );
      })}
      <span className="ml-2 text-[11px] text-[#9ca3af]">0 = Not at all · 4 = Fully</span>
    </div>
  );
};

const DirIcon = ({ dir }) => dir === "up"
  ? <ArrowUp className="h-3.5 w-3.5 text-[#10b981]" />
  : dir === "down"
    ? <ArrowDown className="h-3.5 w-3.5 text-[#ef4444]" />
    : <Minus className="h-3.5 w-3.5 text-[#9ca3af]" />;

/* ════ PAYMENT ════ */
const PaymentView = ({ tenantId, onDone }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-[14px] border border-[#e5e7eb] bg-white overflow-hidden" data-testid="cdu-payment-container">
      <div className="p-6 sm:p-8 flex flex-col lg:flex-row gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-[10px] bg-[#eff6ff] flex items-center justify-center">
              <Shield className="h-6 w-6 text-[#1a56db]" />
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[#111827] tracking-[-0.01em]">MySchoolRx Utility</h2>
              <p className="text-[13px] text-[#6b7280]">K-12 school quality & success</p>
            </div>
          </div>
          <p className="text-[14px] text-[#4b5563] leading-[1.6] mb-5">
            A board- and authorizer-facing diagnostic instrument for evaluating K-12 school quality,
            operational health, and risk across seven pillars.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {["7-pillar quality evaluation","66 diagnostic questions","Quarterly attestation cycles","Contributor/approver workflow","What-If score simulator","AI corrective action plans"].map(i => (
              <div key={i} className="flex items-center gap-2 text-[13px] text-[#374151]">
                <Check className="h-3.5 w-3.5 text-[#1a56db] shrink-0" />
                <span>{i}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:w-[260px] shrink-0 rounded-[12px] border border-[#e5e7eb] bg-[#f9fafb] p-6 flex flex-col items-center justify-center">
          <div className="text-[32px] font-bold text-[#111827]">$995</div>
          <div className="text-[12px] text-[#9ca3af] mb-5">Starting price</div>
          <Button
            onClick={async () => { setBusy(true); try { await api(`tenants/${tenantId}/purchase`, { method: "POST" }); toast.success("Purchased!"); onDone(); } catch { toast.error("Purchase failed"); } finally { setBusy(false); } }}
            disabled={busy}
            className="w-full h-11 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa]"
            data-testid="cdu-purchase-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}Purchase Diagnostic
          </Button>
          <p className="text-[10px] text-[#9ca3af] mt-2 text-center">Simulated for demo</p>
        </div>
      </div>
    </div>
  );
};

/* ════ DOMAIN CARDS — Executive Clarity ════ */
const DomainCards = ({ pillarScores, prevPillarScores }) => (
  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {Object.entries(pillarScores || {}).map(([pk, ps]) => {
      const b = bandOf(ps.band);
      const prev = prevPillarScores?.[pk]?.score;
      const delta = prev !== undefined ? Math.round((ps.score - prev) * 10) / 10 : null;
      const pctBar = Math.min(100, Math.max(0, ps.score));
      return (
        <div key={pk} className="rounded-[12px] border border-[#e5e7eb] bg-white p-4 flex flex-col" data-testid={`pillar-score-${pk}`}>
          <div className="flex items-start justify-between mb-2 gap-2">
            <div className="text-[13px] font-bold text-[#111827] leading-[1.3]">{ps.label}</div>
            <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: b.bg, color: b.text }}>
              {ps.band_label}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-[28px] font-bold leading-none" style={{ color: b.text }}>{ps.score}</span>
            {delta !== null && delta !== 0 && (
              <span className="text-[11px] font-semibold mb-1"
                style={{ color: delta > 0 ? "#059669" : "#b91c1c" }}>
                {delta > 0 ? "+" : ""}{delta}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#9ca3af] mt-1.5">
            {ps.questions_answered}/{ps.questions_total} questions · {Math.round(ps.weight * 100)}%
          </div>
          <div className="mt-3 h-[4px] rounded-full bg-[#f3f4f6] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pctBar}%`, backgroundColor: b.dot }} />
          </div>
        </div>
      );
    })}
  </div>
);

/* ════ SCORE HEADER + RESULTS VIEW ════ */
const ResultsView = ({ data, prevData, compact }) => {
  if (!data) return null;
  const b = bandOf(data.overall_band);
  return (
    <div className="space-y-6">
      {/* Score header card */}
      <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-6 flex flex-col sm:flex-row items-center gap-6">
        <div className="flex flex-col items-center">
          <div className="text-[48px] font-bold leading-none text-[#111827] tracking-[-0.02em]">{data.overall_score}</div>
          <div className="text-[11px] text-[#9ca3af] mt-1">out of 100</div>
        </div>
        <div className="flex-1 text-center sm:text-left">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full mb-2"
            style={{ backgroundColor: b.bg, color: b.text }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: b.dot }} />
            {data.overall_band_label}
          </span>
          <h2 className="text-[20px] font-bold text-[#111827] tracking-[-0.01em]">
            {data.cycle_label || "School Health Score"}
          </h2>
          <p className="text-[12px] text-[#9ca3af] mt-1">
            {data.total_questions_answered}/{data.total_questions} questions
            {data.locked_at ? ` · Locked ${data.locked_at.slice(0, 10)}` : ""}
          </p>
        </div>
        {prevData && (
          <div className="text-center rounded-[10px] border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
            <div className="text-[10px] text-[#9ca3af] uppercase tracking-wider">vs Prior</div>
            <div className="text-[18px] font-bold"
              style={{ color: data.overall_score > prevData.overall_score ? "#059669" : data.overall_score < prevData.overall_score ? "#b91c1c" : "#9ca3af" }}>
              {data.overall_score > prevData.overall_score ? "+" : ""}{Math.round((data.overall_score - prevData.overall_score) * 10) / 10}
            </div>
          </div>
        )}
      </div>

      {!compact && <DomainCards pillarScores={data.pillar_scores} prevPillarScores={prevData?.pillar_scores} />}

      {!compact && data.prioritized_findings?.length > 0 && (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[#e5e7eb]">
            <AlertTriangle className="h-4 w-4 text-[#f59e0b]" />
            <span className="text-[14px] font-semibold text-[#111827]">Priority Findings</span>
          </div>
          <div>
            {data.prioritized_findings.slice(0, 8).map((f, i) => {
              const crit = f.severity === "critical";
              return (
                <div key={i} className={`flex items-start gap-3 px-5 py-3 ${i !== 0 ? "border-t border-[#f3f4f6]" : ""}`}>
                  <span className="h-2 w-2 rounded-full shrink-0 mt-2"
                    style={{ backgroundColor: crit ? "#ef4444" : "#f59e0b" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded"
                        style={{ backgroundColor: crit ? "#fef2f2" : "#fffbeb", color: crit ? "#b91c1c" : "#b45309" }}>
                        {crit ? "Critical" : "Watch"}
                      </span>
                      <span className="text-[10px] text-[#9ca3af] font-mono">{f.pillar_label}</span>
                    </div>
                    <p className="text-[13px] text-[#374151] leading-[1.5]">{f.question_text}</p>
                  </div>
                  <div className="text-[14px] font-bold shrink-0 text-[#111827]">{f.score}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════ OVERVIEW TAB ════ */
const OverviewTab = ({ tenantId, cycles, snapshots, activeCycle }) => {
  const [legacyResults, setLegacyResults] = useState(null);
  const latestSnap = snapshots?.[0];
  const prevSnap = snapshots?.[1];

  useEffect(() => {
    if (!latestSnap) {
      api(`tenants/${tenantId}/results`).then(setLegacyResults).catch(() => {});
    }
  }, [tenantId, latestSnap]);

  const displayData = latestSnap || legacyResults;

  return (
    <div className="space-y-6" data-testid="cdu-overview">
      {displayData && <ResultsView data={displayData} prevData={prevSnap} />}

      {!displayData && activeCycle && (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-[#f9fafb] px-5 py-4 flex items-center gap-3">
          <Clock className="h-5 w-5 text-[#1a56db]" />
          <div>
            <div className="text-[14px] font-semibold text-[#111827]">Active Cycle: {activeCycle.cycle_label}</div>
            <div className="text-[12px] text-[#9ca3af] capitalize">Status: {activeCycle.status}</div>
          </div>
        </div>
      )}
      {!displayData && !activeCycle && (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-10 text-center">
          <Shield className="h-10 w-10 text-[#d1d5db] mx-auto mb-3" />
          <p className="text-[14px] text-[#6b7280]">No completed diagnostics yet. Go to the Diagnostic tab to begin.</p>
        </div>
      )}

      {/* Risk Legend */}
      <div className="rounded-[10px] border border-[#e5e7eb] bg-white px-4 py-3 flex items-center justify-center gap-8 flex-wrap">
        {[["green", "Healthy (80-100)"], ["yellow", "Caution (60-79)"], ["red", "High Risk (0-59)"]].map(([k, l]) => (
          <div key={k} className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BAND[k].dot }} />
            <span className="text-[11px] text-[#6b7280] font-medium">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ════ HISTORY TAB ════ */
const HistoryTab = ({ snapshots, trend }) => {
  const [selected, setSelected] = useState(null);
  if (selected) {
    const idx = snapshots.findIndex(s => s.id === selected);
    const prev = idx < snapshots.length - 1 ? snapshots[idx + 1] : null;
    const snap = snapshots[idx];
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelected(null)}
          className="text-[12px] font-medium text-[#1a56db] flex items-center gap-1 hover:underline"
        >
          <ChevronRight className="h-3 w-3 rotate-180" /> Back to History
        </button>
        <ResultsView data={snap} prevData={prev} />
      </div>
    );
  }

  const hasItems = trend?.quarterly_trend?.length > 0;

  return (
    <div className="space-y-5" data-testid="cdu-history">
      <h3 className="text-[18px] font-bold text-[#111827] flex items-center gap-2 tracking-[-0.01em]">
        <History className="h-5 w-5 text-[#1a56db]" />Quarterly History
      </h3>

      {!hasItems ? (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-12 text-center">
          <div className="h-14 w-14 rounded-full bg-[#eff6ff] flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-7 w-7 text-[#1a56db]" />
          </div>
          <h4 className="text-[16px] font-bold text-[#111827] mb-2">Quarterly History</h4>
          <p className="text-[13px] text-[#9ca3af] max-w-sm mx-auto leading-[1.6]">
            No completed quarterly reviews yet. History will appear here after the first review cycle is completed.
          </p>
        </div>
      ) : (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden">
          {trend.quarterly_trend.map((t, i) => {
            const b = bandOf(t.overall_band);
            const snap = snapshots.find(s => s.cycle_label === t.cycle_label);
            return (
              <button
                key={i}
                onClick={() => snap && setSelected(snap.id)}
                className={`w-full text-left px-5 py-4 flex items-center justify-between hover:bg-[#f9fafb] transition-colors ${i !== 0 ? "border-t border-[#f3f4f6]" : ""}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-[26px] font-bold" style={{ color: b.text }}>{t.overall_score}</div>
                  <div>
                    <div className="text-[14px] font-semibold text-[#111827]">{t.cycle_label}</div>
                    <div className="text-[11px] text-[#9ca3af]">{t.locked_at?.slice(0, 10)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: b.bg, color: b.text }}>{b.label}</span>
                  {t.direction !== "baseline" && (
                    <div className="flex items-center gap-1">
                      <DirIcon dir={t.direction} />
                      <span className="text-[12px] font-semibold text-[#374151]">{t.score_change > 0 ? "+" : ""}{t.score_change}</span>
                    </div>
                  )}
                  <ChevronRight className="h-4 w-4 text-[#9ca3af]" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {trend?.intervention_reviews?.length > 0 && (
        <>
          <h3 className="text-[14px] font-semibold text-[#111827] mt-8 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#f59e0b]" />Intervention Reviews
          </h3>
          <div className="rounded-[12px] border border-[#fde68a] bg-[#fffbeb] overflow-hidden">
            {trend.intervention_reviews.map((r, i) => (
              <div key={i} className={`px-5 py-3 flex items-center gap-3 ${i !== 0 ? "border-t border-[#fde68a]/60" : ""}`}>
                <span className="text-[20px] font-bold text-[#b45309]">{r.overall_score}</span>
                <div>
                  <div className="text-[13px] text-[#111827]">{r.cycle_label}</div>
                  <div className="text-[11px] text-[#9ca3af]">{r.locked_at?.slice(0, 10)}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ════ COLLAPSIBLE DOMAIN GROUP ════ */
const DomainGroup = ({ label, count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-[10px] border border-[#e5e7eb] bg-white overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#f9fafb] hover:bg-[#f3f4f6] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-bold text-[#111827]">{label}</span>
          {count !== undefined && (
            <span className="text-[11px] text-[#9ca3af] font-medium">({count})</span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-[#6b7280] transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
};

/* ════ WHAT-IF TAB ════ */
const WhatIfTab = ({ tenantId }) => {
  const [scenarios, setScenarios] = useState([]);
  const [name, setName] = useState("My Scenario");
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    govApi(`tenants/${tenantId}/whatif`).then(d => setScenarios(d.scenarios || [])).catch(() => {});
    api(`tenants/${tenantId}/questions`).then(d => {
      const qs = [];
      for (const p of d.pillars || []) for (const sd of p.subdomains || []) for (const q of sd.questions || []) qs.push({ ...q, pillar_label: p.label, pillar_key: p.key });
      setQuestions(qs);
    }).catch(() => {});
  }, [tenantId]);

  const handleCreate = async () => {
    if (Object.keys(overrides).length === 0) { toast.info("Change at least one question value"); return; }
    setSaving(true);
    try {
      const r = await post(`tenants/${tenantId}/whatif`, { scenario_name: name, scenario_values: overrides });
      setResult(r);
      setScenarios(prev => [r, ...prev]);
      toast.success("Scenario created!");
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  if (result) {
    const off = result.official_scores;
    const sim = result.simulated_scores;
    const d = result.deltas;
    const offB = bandOf(off.overall_band);
    return (
      <div className="space-y-5" data-testid="cdu-whatif-result">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-[18px] font-bold text-[#111827] flex items-center gap-2 tracking-[-0.01em]">
            <FlaskConical className="h-5 w-5 text-[#1a56db]" />What-If: {result.scenario_name}
          </h3>
          <div className="flex gap-2 items-center">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#eff6ff] text-[#1a56db] tracking-[0.05em]">
              SIMULATION ONLY — UNOFFICIAL
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResult(null)}
              className="h-8 rounded-[6px] border-[#e5e7eb] text-[#374151] text-[12px]"
            >New Scenario</Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-5 text-center">
            <div className="text-[11px] text-[#9ca3af] uppercase tracking-wider mb-2">Official Score</div>
            <div className="text-[40px] font-bold leading-none" style={{ color: offB.text }}>{off.overall_score}</div>
            <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: offB.bg, color: offB.text }}>{off.overall_band_label}</span>
          </div>
          <div className="rounded-[12px] border border-[#bfdbfe] bg-[#eff6ff] p-5 text-center">
            <div className="text-[11px] text-[#1a56db] uppercase tracking-wider mb-2 font-semibold">Simulated Score</div>
            <div className="text-[40px] font-bold leading-none text-[#1a56db]">{sim.overall_score}</div>
            <div className="text-[13px] font-semibold mt-2"
              style={{ color: d.overall > 0 ? "#059669" : d.overall < 0 ? "#b91c1c" : "#9ca3af" }}>
              {d.overall > 0 ? "+" : ""}{d.overall} points
            </div>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(sim.pillar_scores || {}).map(([pk, ps]) => {
            const offP = off.pillar_scores?.[pk];
            const delta = d.pillars?.[pk] || 0;
            const changed = delta !== 0;
            return (
              <div key={pk} className={`rounded-[10px] border p-3 bg-white ${changed ? "border-[#bfdbfe]" : "border-[#e5e7eb]"}`}>
                <div className="text-[12px] text-[#374151] font-semibold leading-[1.3]">{ps.label}</div>
                <div className="flex items-end gap-2 mt-2">
                  <span className="text-[#9ca3af] text-[13px]">{offP?.score || 0}</span>
                  <ArrowRight className="h-3 w-3 text-[#d1d5db] mb-1" />
                  <span className="text-[18px] font-bold"
                    style={{ color: delta > 0 ? "#059669" : delta < 0 ? "#b91c1c" : "#111827" }}>{ps.score}</span>
                  {changed && <span className="text-[10px] font-semibold mb-1"
                    style={{ color: delta > 0 ? "#059669" : "#b91c1c" }}>{delta > 0 ? "+" : ""}{delta}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Group questions by pillar
  const byPillar = {};
  for (const q of questions) {
    const key = q.pillar_key || q.pillar_label || "Other";
    const lab = q.pillar_label || key;
    if (!byPillar[key]) byPillar[key] = { label: lab, items: [] };
    byPillar[key].items.push(q);
  }

  return (
    <div className="space-y-5" data-testid="cdu-whatif">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-[18px] font-bold text-[#111827] flex items-center gap-2 tracking-[-0.01em]">
          <FlaskConical className="h-5 w-5 text-[#1a56db]" />What-If Simulator
        </h3>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#eff6ff] text-[#1a56db] tracking-[0.05em]">
          SIMULATION ONLY
        </span>
      </div>

      <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-5">
        <div className="flex gap-3 items-center flex-wrap">
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Scenario name"
            className="h-10 w-full sm:w-[280px] rounded-[8px] border-[#e5e7eb] text-[14px] bg-white focus-visible:ring-2 focus-visible:ring-[#1a56db]/20 focus-visible:border-[#1a56db]"
            data-testid="whatif-name"
          />
          <Button
            onClick={handleCreate}
            disabled={saving}
            className="h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-5"
            data-testid="whatif-create-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}Run Simulation
          </Button>
        </div>
        <p className="text-[12px] text-[#9ca3af] mt-3">
          Change question scores below to see the impact on your overall and pillar scores. Only changed values are used.
        </p>
      </div>

      <div className="space-y-3">
        {Object.entries(byPillar).map(([key, grp]) => (
          <DomainGroup key={key} label={grp.label} count={grp.items.length} defaultOpen={false}>
            <div>
              {grp.items.map((q, i) => (
                <div key={q.question_id} className={`px-4 py-3 ${i !== 0 ? "border-t border-[#f3f4f6]" : ""}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] text-[#9ca3af] font-mono font-semibold bg-[#f3f4f6] px-2 py-0.5 rounded shrink-0 mt-0.5">{q.question_id}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-[#374151] leading-[1.5] mb-2">
                        {q.question_text?.slice(0, 140)}{q.question_text?.length > 140 ? "..." : ""}
                      </p>
                      {q.response_type === "standard_0_to_4_quality_scale" && (
                        <ScaleInput size="sm" value={overrides[q.question_id]} onChange={v => setOverrides(p => ({ ...p, [q.question_id]: v }))} qid={q.question_id} />
                      )}
                      {q.response_type === "yes_no_scale" && (
                        <div className="flex gap-2">
                          {[{v:1,l:"Yes"},{v:0,l:"No"}].map(o => (
                            <button
                              key={o.v}
                              onClick={() => setOverrides(p => ({ ...p, [q.question_id]: o.v }))}
                              className={`px-4 h-8 rounded-[8px] text-[12px] font-semibold border transition-all ${overrides[q.question_id] === o.v
                                ? "bg-[#1a56db] text-white border-[#1a56db]"
                                : "bg-[#f9fafb] text-[#4b5563] border-[#e5e7eb] hover:bg-[#eff6ff] hover:border-[#bfdbfe]"}`}
                            >{o.l}</button>
                          ))}
                        </div>
                      )}
                      {["percentage","number","integer"].includes(q.response_type) && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={overrides[q.question_id] ?? ""}
                            onChange={e => setOverrides(p => ({ ...p, [q.question_id]: e.target.value === "" ? undefined : Number(e.target.value) }))}
                            placeholder="Enter value"
                            className="h-9 w-32 rounded-[8px] border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#1a56db] focus:ring-[3px] focus:ring-[#1a56db]/10"
                          />
                          {q.response_type === "percentage" && <span className="text-[13px] text-[#6b7280]">%</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </DomainGroup>
        ))}
      </div>

      {scenarios.length > 0 && (
        <div>
          <h4 className="text-[14px] font-bold text-[#111827] mb-2.5">Saved Scenarios</h4>
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden">
            {scenarios.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setResult(s)}
                className={`w-full flex items-center justify-between px-5 py-3 hover:bg-[#f9fafb] transition-colors text-left ${i !== 0 ? "border-t border-[#f3f4f6]" : ""}`}
              >
                <div>
                  <div className="text-[13px] font-semibold text-[#111827]">{s.scenario_name}</div>
                  <div className="text-[11px] text-[#9ca3af]">{s.created_at?.slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[12px] text-[#6b7280]">{s.official_scores?.overall_score} → {s.simulated_scores?.overall_score}</span>
                  <span className="text-[12px] font-bold"
                    style={{ color: s.deltas?.overall > 0 ? "#059669" : "#b91c1c" }}>
                    {s.deltas?.overall > 0 ? "+" : ""}{s.deltas?.overall}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════ ACTION PLAN TAB ════ */
const ActionPlanTab = ({ tenantId, snapshots }) => {
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [nextDate, setNextDate] = useState("");

  useEffect(() => {
    govApi(`tenants/${tenantId}/action-plans`).then(d => {
      setPlans(d.plans || []);
      if (d.plans?.[0]) loadPlan(d.plans[0].id);
    }).catch(() => {});
  }, [tenantId]);

  const loadPlan = async (planId) => {
    const d = await govApi(`tenants/${tenantId}/action-plans/${planId}`);
    setActivePlan(d.plan);
    setTasks(d.tasks || []);
    setStats(d.stats);
  };

  const handleGenerate = async () => {
    if (!snapshots?.[0]) { toast.error("No approved snapshot to generate from"); return; }
    if (!nextDate) { toast.error("Enter the next diagnostic date"); return; }
    setGenerating(true);
    try {
      const d = await post(`tenants/${tenantId}/action-plans/generate`, { snapshot_id: snapshots[0].id, next_diagnostic_date: nextDate });
      toast.success(`Plan generated with ${d.tasks_created} tasks!`);
      setPlans(prev => [d.plan, ...prev]);
      loadPlan(d.plan.id);
    } catch { toast.error("Generation failed"); } finally { setGenerating(false); }
  };

  const updateTask = async (taskId, updates) => {
    await patch(`tenants/${tenantId}/action-plans/${activePlan.id}/tasks/${taskId}`, updates);
    loadPlan(activePlan.id);
  };

  const STATUS_OPTS = ["not_started", "in_progress", "blocked", "complete", "verified"];
  const STATUS_COLOR = { not_started: "#9ca3af", in_progress: "#1a56db", blocked: "#b91c1c", complete: "#059669", verified: "#0891b2" };

  return (
    <div className="space-y-5" data-testid="cdu-action-plan">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-[18px] font-bold text-[#111827] flex items-center gap-2 tracking-[-0.01em]">
          <ListTodo className="h-5 w-5 text-[#1a56db]" />Corrective Action Plan
        </h3>
        {!activePlan && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={nextDate}
              onChange={e => setNextDate(e.target.value)}
              className="h-9 rounded-[8px] border border-[#e5e7eb] bg-white px-3 text-[12px] text-[#111827]"
              data-testid="action-plan-date"
            />
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="h-9 rounded-[8px] bg-[#1a56db] text-white text-[12px] font-semibold hover:bg-[#143faa]"
              data-testid="generate-plan-btn"
            >
              {generating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}Generate Plan
            </Button>
          </div>
        )}
      </div>

      {!activePlan ? (
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-10 text-center">
          <ListTodo className="h-10 w-10 text-[#d1d5db] mx-auto mb-3" />
          <p className="text-[14px] text-[#6b7280]">No action plan yet. Generate one from your latest approved diagnostic.</p>
        </div>
      ) : (
        <>
          {/* Plan Summary */}
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[14px] font-semibold text-[#111827]">{activePlan.plan_summary?.slice(0, 120)}</div>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                style={activePlan.activated_flag
                  ? { backgroundColor: "#ecfdf5", color: "#059669" }
                  : { backgroundColor: "#fffbeb", color: "#b45309" }}
                data-testid="plan-status-badge">
                {activePlan.activated_flag ? "Active" : "Draft"}
              </span>
            </div>
            <div className="text-[12px] text-[#9ca3af] mt-2">
              Target: {activePlan.next_diagnostic_target_date} · {activePlan.total_tasks} tasks
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                ["Total", stats.total, "#111827"],
                ["Not Started", stats.not_started, "#9ca3af"],
                ["In Progress", stats.in_progress, "#1a56db"],
                ["Blocked", stats.blocked, "#b91c1c"],
                ["Complete", stats.complete, "#059669"],
                ["Overdue", stats.overdue, "#b91c1c"],
              ].map(([l, v, c]) => (
                <div key={l} className="rounded-[10px] border border-[#e5e7eb] bg-white p-3 text-center">
                  <div className="text-[20px] font-bold" style={{ color: c }}>{v}</div>
                  <div className="text-[10px] text-[#9ca3af] font-medium uppercase tracking-wider mt-0.5">{l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Task List */}
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white overflow-hidden">
            {tasks.map((t, i) => (
              <div key={t.id} className={`px-5 py-4 ${i !== 0 ? "border-t border-[#f3f4f6]" : ""} ${t.status === "blocked" ? "bg-[#fef2f2]/40" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      {t.linked_pillar && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#f3f4f6] text-[#6b7280]">{t.linked_pillar}</span>
                      )}
                      <span className="text-[14px] font-semibold text-[#111827]">{t.task_title}</span>
                    </div>
                    {t.task_description && <p className="text-[12px] text-[#6b7280] leading-[1.55]">{t.task_description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-[#9ca3af]">
                      <span>Due: {t.due_date}</span>
                      <span>Owner: {t.owner_manual_name || t.owner_user_id || "Unassigned"}</span>
                    </div>
                  </div>
                  <select
                    value={t.status}
                    onChange={e => updateTask(t.id, { status: e.target.value })}
                    className="h-8 rounded-[6px] border border-[#e5e7eb] bg-white px-2 text-[11px] font-semibold"
                    style={{ color: STATUS_COLOR[t.status] || "#6b7280" }}
                    data-testid={`task-status-${t.id}`}
                  >
                    {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {!activePlan.activated_flag && (
            <Button
              onClick={async () => { await patch(`tenants/${tenantId}/action-plans/${activePlan.id}`, { activated_flag: true, editable_draft_flag: false }); loadPlan(activePlan.id); toast.success("Plan activated!"); }}
              className="h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-5"
              data-testid="activate-plan-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />Activate Plan
            </Button>
          )}
        </>
      )}
    </div>
  );
};

/* ════ DIAGNOSTIC TAB ════ */
const DiagnosticTab = ({ tenantId, status, cycles, refresh }) => {
  const [starting, setStarting] = useState(false);
  const [pillars, setPillars] = useState([]);
  const [activePillar, setActivePillar] = useState(null);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(66);
  const [govResponses, setGovResponses] = useState([]);
  const [approvals, setApprovals] = useState({});
  const [approving, setApproving] = useState(false);
  const [locking, setLocking] = useState(false);

  const activeCycle = cycles.find(c => ["active", "in_progress", "pending_approval"].includes(c.status));
  const lockedCycle = cycles.find(c => c.status === "locked");
  const sessionStatus = status?.session?.status;
  const isCompleted = status?.status === "completed" || status?.has_results || sessionStatus === "completed";
  const isSessionInProgress = sessionStatus === "in_progress" && !isCompleted;

  let phase = "not_started";
  if (lockedCycle) phase = "locked";
  else if (isCompleted) phase = "review";
  else if (isSessionInProgress) phase = "entering";
  else if (status?.status === "paid") phase = "not_started";

  const loadQuestions = useCallback(async () => {
    try {
      const resp = await api(`tenants/${tenantId}/questions`);
      setPillars(resp.pillars || []);
      setTotalAnswered(resp.total_answered || 0);
      setTotalQuestions(resp.total_questions || 66);
      const existing = {};
      for (const p of resp.pillars || [])
        for (const sd of p.subdomains || [])
          for (const q of sd.questions || [])
            if (q.response) existing[q.question_id] = q.response.response_value;
      setResponses(existing);
      if (!activePillar && resp.pillars?.length > 0) setActivePillar(resp.pillars[0].key);
    } catch {}
  }, [tenantId, activePillar]);

  useEffect(() => {
    if (phase === "entering" || phase === "review") {
      loadQuestions();
      if (phase === "review" && activeCycle) {
        govApi(`tenants/${tenantId}/cycles/${activeCycle.id}/responses`).then(d => setGovResponses(d.responses || [])).catch(() => {});
      }
    }
  }, [phase, loadQuestions, tenantId, activeCycle]);

  const handleStart = async () => {
    setStarting(true);
    try {
      if (!activeCycle) {
        await post(`tenants/${tenantId}/cycles/generate-quarterly`, { year: new Date().getFullYear() });
        const cyc = await govApi(`tenants/${tenantId}/cycles`);
        const active = (cyc.cycles || []).find(c => c.status === "active");
        if (active) await post(`tenants/${tenantId}/cycles/${active.id}/launch`);
      } else if (!activeCycle.session_id) {
        await post(`tenants/${tenantId}/cycles/${activeCycle.id}/launch`);
      }
      if (status?.status === "paid" && !status?.session)
        await api(`tenants/${tenantId}/start`, { method: "POST" });
      toast.success("Diagnostic started!");
      refresh();
    } catch (e) { toast.error(e.message || "Failed to start"); } finally { setStarting(false); }
  };

  const handleSavePillar = async (pillarKey) => {
    const pillar = pillars.find(p => p.key === pillarKey);
    if (!pillar) return;
    const pr = [];
    for (const sd of pillar.subdomains || [])
      for (const q of sd.questions || []) {
        const val = responses[q.question_id];
        if (val !== null && val !== undefined) pr.push({ question_id: q.question_id, response_value: val });
      }
    if (pr.length === 0) { toast.info("No responses to save"); return; }
    setSaving(true);
    try {
      const r = await api(`tenants/${tenantId}/responses`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ responses: pr }) });
      setTotalAnswered(r.total_answered);
      toast.success(`Saved ${r.saved} responses`);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleSubmitForReview = async () => {
    const allR = [];
    for (const p of pillars)
      for (const sd of p.subdomains || [])
        for (const q of sd.questions || []) {
          const val = responses[q.question_id];
          if (val !== null && val !== undefined) allR.push({ question_id: q.question_id, response_value: val });
        }
    setSubmitting(true);
    try {
      if (allR.length > 0) await api(`tenants/${tenantId}/responses`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ responses: allR }) });
      await api(`tenants/${tenantId}/complete`, { method: "POST" });
      if (activeCycle) {
        const allPillars = ["P1","P2","P3","P4","P5","P6","P7"];
        await post(`tenants/${tenantId}/cycles/${activeCycle.id}/assignments`, {
          user_id: "portal_user", user_name: "Portal User", user_email: "portal@school.com", role: "contributor", assigned_pillars: allPillars
        }).catch(() => {});
        await post(`tenants/${tenantId}/cycles/${activeCycle.id}/assignments`, {
          user_id: "approver_portal", user_name: "Approver", user_email: "approver@school.com", role: "approver", assigned_pillars: allPillars
        }).catch(() => {});
        const govR = allR.map(r => ({ ...r, user_id: "portal_user", user_name: "Portal User", user_role: "contributor" }));
        await post(`tenants/${tenantId}/cycles/${activeCycle.id}/responses`, { responses: govR }).catch(() => {});
      }
      toast.success("Submitted for review!");
      refresh();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleApproveAll = async () => {
    let cycleId = activeCycle?.id;
    if (!cycleId) {
      const cyc = await govApi(`tenants/${tenantId}/cycles`).catch(() => ({ cycles: [] }));
      const ip = (cyc.cycles || []).find(c => ["active", "in_progress", "pending_approval"].includes(c.status));
      cycleId = ip?.id;
    }
    if (!cycleId) { toast.error("No active cycle"); return; }
    setApproving(true);
    try {
      const allP = ["P1","P2","P3","P4","P5","P6","P7"];
      await post(`tenants/${tenantId}/cycles/${cycleId}/assignments`, { user_id: "portal_user", user_name: "Portal User", user_email: "p@s.com", role: "contributor", assigned_pillars: allP }).catch(() => {});
      await post(`tenants/${tenantId}/cycles/${cycleId}/assignments`, { user_id: "approver_portal", user_name: "Approver", user_email: "a@s.com", role: "approver", assigned_pillars: allP }).catch(() => {});
      const govR = [];
      for (const p of pillars) for (const sd of p.subdomains || []) for (const q of sd.questions || []) { const v = responses[q.question_id]; if (v !== null && v !== undefined) govR.push({ question_id: q.question_id, response_value: v, user_id: "portal_user", user_name: "Portal User", user_role: "contributor" }); }
      if (govR.length > 0) await post(`tenants/${tenantId}/cycles/${cycleId}/responses`, { responses: govR }).catch(() => {});
      const gd = await govApi(`tenants/${tenantId}/cycles/${cycleId}/responses`).catch(() => ({ responses: [] }));
      const grList = gd.responses || [];
      if (grList.length === 0) { toast.error("No responses to approve"); setApproving(false); return; }
      const actions = grList.map(r => { const a = approvals[r.question_id]; if (a && a.action === "override") return { question_id: r.question_id, action: "override", override_value: a.override_value, override_reason: a.override_reason || "Corrected", comment: a.comment || "" }; return { question_id: r.question_id, action: "approve", comment: a?.comment || "" }; });
      const result = await post(`tenants/${tenantId}/cycles/${cycleId}/approve`, { actions, approver_user_id: "approver_portal", approver_name: "Approver", approver_role: "approver" });
      toast.success(`Approved ${result.approved} responses!`);
      refresh();
    } catch (e) { toast.error(e.message); } finally { setApproving(false); }
  };

  const handleLock = async () => {
    let cid = activeCycle?.id || lockedCycle?.id;
    if (!cid) {
      const cyc = await govApi(`tenants/${tenantId}/cycles`).catch(() => ({ cycles: [] }));
      const ip = (cyc.cycles || []).find(c => ["in_progress", "pending_approval"].includes(c.status));
      cid = ip?.id;
    }
    if (!cid) { toast.error("No cycle to lock"); return; }
    setLocking(true);
    try {
      const result = await post(`tenants/${tenantId}/cycles/${cid}/lock`, {});
      toast.success(`Locked! Score: ${result.overall_score}/100 (${result.overall_band_label})`);
      refresh();
    } catch (e) { toast.error(e.message); } finally { setLocking(false); }
  };

  // ═══ PHASE: Not Started ═══
  if (phase === "not_started") {
    return (
      <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-10 text-center space-y-5" data-testid="cdu-start-container">
        <div className="h-14 w-14 rounded-full bg-[#eff6ff] flex items-center justify-center mx-auto">
          <Shield className="h-7 w-7 text-[#1a56db]" />
        </div>
        <h2 className="text-[20px] font-bold text-[#111827] tracking-[-0.01em]">Ready to Begin Diagnostic</h2>
        <p className="text-[14px] text-[#6b7280] max-w-md mx-auto">Start the assessment to evaluate your school across all 7 pillars (66 questions).</p>
        <Button
          onClick={handleStart}
          disabled={starting}
          className="h-11 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-6"
          data-testid="cdu-start-btn"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}Begin Diagnostic
        </Button>
      </div>
    );
  }

  // ═══ PHASE: Locked ═══
  if (phase === "locked") {
    return (
      <div className="rounded-[12px] border border-[#a7f3d0] bg-[#ecfdf5] p-5 flex items-center gap-3" data-testid="cdu-locked">
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0">
          <Lock className="h-5 w-5 text-[#059669]" />
        </div>
        <div className="flex-1">
          <div className="text-[14px] font-semibold text-[#111827]">Diagnostic Locked & Finalized</div>
          <div className="text-[12px] text-[#059669]">View results in the Overview tab</div>
        </div>
        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white text-[#059669] tracking-[0.05em]">OFFICIAL</span>
      </div>
    );
  }

  // ═══ PHASE: Review & Approve ═══
  if (phase === "review") {
    const displayR = govResponses.length > 0 ? govResponses : Object.entries(responses).map(([qid, val]) => ({ question_id: qid, response_value: val, pillar: qid.replace(/Q\d+/, ""), approval_status: "pending" }));
    const approvedCount = displayR.filter(r => ["approved", "overridden"].includes(r.approval_status)).length;
    const overriddenCount = displayR.filter(r => r.approval_status === "overridden").length;
    const allApproved = displayR.length > 0 && approvedCount >= displayR.length;

    return (
      <div className="space-y-5" data-testid="cdu-review">
        {/* Review header */}
        <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-[8px] bg-[#fffbeb] flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-[#d97706]" />
              </div>
              <div>
                <div className="text-[16px] font-bold text-[#111827]">Review & Approve Responses</div>
                <div className="text-[12px] text-[#9ca3af]">{approvedCount} of {displayR.length} reviewed</div>
              </div>
            </div>
            <div className="flex gap-2">
              {!allApproved && (
                <Button
                  onClick={handleApproveAll}
                  disabled={approving}
                  className="h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-5"
                  data-testid="cdu-approve-all-btn"
                >
                  {approving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}Approve All
                </Button>
              )}
              {(allApproved || displayR.length === 0) && activeCycle && (
                <Button
                  onClick={handleLock}
                  disabled={locking}
                  className="h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-5"
                  data-testid="cdu-lock-btn"
                >
                  {locking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}Lock & Finalize
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ["Total", displayR.length, "#111827"],
            ["Pending", displayR.length - approvedCount, "#d97706"],
            ["Approved", approvedCount, "#059669"],
            ["Overridden", overriddenCount, "#7c3aed"],
          ].map(([l, v, c]) => (
            <div key={l} className="rounded-[10px] border border-[#e5e7eb] bg-white p-4 text-center">
              <div className="text-[24px] font-bold" style={{ color: c }}>{v}</div>
              <div className="text-[10px] font-semibold text-[#9ca3af] uppercase tracking-wider mt-0.5">{l}</div>
            </div>
          ))}
        </div>

        {/* Collapsible domain groups of questions */}
        {pillars.map(p => {
          const pResps = displayR.filter(r => r.pillar === p.key || r.question_id?.startsWith(p.key));
          if (pResps.length === 0) return null;
          const allQs = [];
          for (const sd of p.subdomains || []) for (const q of sd.questions || []) allQs.push(q);
          return (
            <DomainGroup key={p.key} label={p.label} count={pResps.length}>
              <div>
                {pResps.map((r, i) => {
                  const q = allQs.find(q => q.question_id === r.question_id);
                  const isAppr = ["approved", "overridden"].includes(r.approval_status);
                  const ovr = approvals[r.question_id];
                  return (
                    <div key={r.question_id} className={`px-5 py-4 ${i !== 0 ? "border-t border-[#f3f4f6]" : ""}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[10px] text-[#9ca3af] font-mono font-semibold bg-[#f3f4f6] px-2 py-0.5 rounded">{r.question_id}</span>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={isAppr
                                ? { backgroundColor: "#ecfdf5", color: "#059669" }
                                : { backgroundColor: "#fffbeb", color: "#d97706" }}>
                              {r.approval_status || "pending"}
                            </span>
                          </div>
                          <p className="text-[13px] text-[#374151] leading-[1.55]">{q?.question_text || r.question_id}</p>
                        </div>
                        <div className="flex items-start gap-3 shrink-0">
                          <div className="text-right">
                            <div className="text-[10px] text-[#9ca3af] uppercase tracking-wider">Value</div>
                            <div className="text-[20px] font-bold text-[#111827] leading-none">{r.response_value}</div>
                          </div>
                          {!isAppr && (
                            <div className="flex flex-col gap-1.5 items-end min-w-[120px]">
                              <button
                                onClick={() => setApprovals(p => ({ ...p, [r.question_id]: { action: "approve" } }))}
                                className="w-full h-8 rounded-[6px] text-[11px] font-semibold border border-[#a7f3d0] text-[#059669] hover:bg-[#ecfdf5] transition-colors flex items-center justify-center gap-1"
                                data-testid={`approve-${r.question_id}`}
                              >
                                <Check className="h-3 w-3" />Approve
                              </button>
                              {(!ovr || ovr.action !== "override") ? (
                                <button
                                  onClick={() => setApprovals(p => ({ ...p, [r.question_id]: { action: "override", override_value: r.response_value, override_reason: "" } }))}
                                  className="w-full h-8 rounded-[6px] text-[11px] font-semibold border border-[#e9d5ff] text-[#7c3aed] hover:bg-[#faf5ff] transition-colors"
                                >
                                  Override
                                </button>
                              ) : (
                                <div className="flex flex-col gap-1.5 w-full">
                                  <div className="flex gap-1">
                                    {SCALES.map(s => (
                                      <button
                                        key={s.value}
                                        onClick={() => setApprovals(p => ({ ...p, [r.question_id]: { ...p[r.question_id], override_value: s.value } }))}
                                        className={`h-7 w-7 rounded text-[11px] font-bold transition-all ${ovr.override_value === s.value
                                          ? "bg-[#7c3aed] text-white border-[#7c3aed]"
                                          : "bg-white text-[#6b7280] border border-[#e5e7eb] hover:bg-[#faf5ff]"}`}
                                      >{s.value}</button>
                                    ))}
                                  </div>
                                  <input
                                    placeholder="Reason (required)"
                                    value={ovr.override_reason || ""}
                                    onChange={e => setApprovals(p => ({ ...p, [r.question_id]: { ...p[r.question_id], override_reason: e.target.value } }))}
                                    className="h-7 w-full text-[11px] rounded-[6px] border border-[#e5e7eb] bg-white px-2 text-[#111827] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#7c3aed] focus:ring-[3px] focus:ring-[#7c3aed]/10"
                                    data-testid={`override-reason-${r.question_id}`}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          {isAppr && <CheckCircle className="h-6 w-6 text-[#10b981] shrink-0 mt-1" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DomainGroup>
          );
        })}
      </div>
    );
  }

  // ═══ PHASE: Entering — Question Taker ═══
  const answeredCount = totalAnswered;
  const progressPct = Math.round((answeredCount / Math.max(totalQuestions, 1)) * 100);
  const currentPillar = pillars.find(p => p.key === activePillar);
  const currentIdx = pillars.findIndex(p => p.key === activePillar);

  return (
    <div className="space-y-4" data-testid="cdu-assessment-taker">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT: Pillar Navigation */}
        <aside className="lg:w-[300px] shrink-0" data-testid="cdu-pillar-nav">
          <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-5 space-y-5">
            {/* Header */}
            <div>
              <h2 className="text-[16px] font-bold text-[#111827] tracking-[-0.01em]">MySchoolRx</h2>
              <p className="text-[12px] text-[#9ca3af] mt-0.5">66 questions across 7 pillars</p>
            </div>

            {/* Progress Ring */}
            <div className="flex items-center gap-4">
              <div className="relative h-16 w-16 shrink-0">
                <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#f3f4f6" strokeWidth="5" />
                  <circle
                    cx="32" cy="32" r="28" fill="none"
                    stroke="#1a56db" strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(progressPct / 100) * 2 * Math.PI * 28} ${2 * Math.PI * 28}`}
                    style={{ transition: "stroke-dasharray 0.4s ease" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[12px] font-bold text-[#111827]">{progressPct}%</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-[#111827]">{answeredCount} of {totalQuestions} answered</div>
                <div className="text-[11px] text-[#9ca3af] truncate">{currentPillar?.label || "—"} in progress</div>
              </div>
            </div>

            {/* Pillar list */}
            <div className="space-y-1.5">
              {pillars.map((p, idx) => {
                const isActive = activePillar === p.key;
                const pctDone = p.question_count > 0 ? Math.round((p.answered_count / p.question_count) * 100) : 0;
                const isDone = pctDone === 100;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setActivePillar(p.key)}
                    className={`w-full text-left p-2.5 rounded-[8px] transition-all border ${
                      isActive
                        ? "bg-[#eff6ff] border-[#bfdbfe]"
                        : "bg-white border-transparent hover:bg-[#f9fafb]"
                    }`}
                    data-testid={`cdu-pillar-btn-${p.key}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Numbered indicator */}
                      <span
                        className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[12px] font-bold border-2 transition-all ${
                          isDone
                            ? "bg-[#10b981] border-[#10b981] text-white"
                            : isActive
                              ? "bg-[#1a56db] border-[#1a56db] text-white"
                              : "bg-white border-[#d1d5db] text-[#6b7280]"
                        }`}
                      >
                        {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[13px] leading-[1.3] truncate ${
                          isActive ? "font-bold text-[#1a56db]" : isDone ? "font-semibold text-[#111827]" : "font-medium text-[#374151]"
                        }`}>{p.label}</div>
                        {/* Mini per-pillar progress bar */}
                        <div className="mt-1.5 h-[3px] rounded-full bg-[#f3f4f6] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pctDone}%`,
                              backgroundColor: isDone ? "#10b981" : isActive ? "#1a56db" : "#d1d5db",
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-[10px] text-[#9ca3af] shrink-0 font-medium">{p.answered_count}/{p.question_count}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-[#e5e7eb]">
              <Button
                onClick={handleSubmitForReview}
                disabled={submitting || totalAnswered === 0}
                className="w-full h-11 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] disabled:opacity-50"
                data-testid="cdu-submit-review-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}Submit for Review
              </Button>
              <p className="text-[11px] text-[#9ca3af] text-center mt-2">Saves, scores, and submits for approval</p>
            </div>
          </div>
        </aside>

        {/* RIGHT: Question area */}
        <div className="flex-1 min-w-0">
          {currentPillar ? (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h3 className="text-[22px] font-bold text-[#111827] tracking-[-0.01em] leading-[1.2]">{currentPillar.label}</h3>
                  <p className="text-[14px] text-[#6b7280] mt-1">{currentPillar.description}</p>
                </div>
                <Button
                  onClick={() => handleSavePillar(currentPillar.key)}
                  disabled={saving}
                  className="h-9 rounded-[6px] bg-[#eff6ff] text-[#1a56db] border border-[#bfdbfe] text-[12px] font-semibold hover:bg-[#dbeafe] shrink-0 px-4"
                  data-testid="cdu-save-pillar-btn"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Save Pillar
                </Button>
              </div>

              {/* Subdomains */}
              {currentPillar.subdomains?.map(sd => (
                <div key={sd.subdomain_id} className="space-y-4">
                  {/* Subgroup header */}
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#e5e7eb]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
                    <h4 className="text-[14px] font-bold text-[#111827] tracking-[-0.005em]">{sd.subdomain_name}</h4>
                  </div>

                  {/* Questions */}
                  <div className="space-y-3">
                    {sd.questions?.map((q) => {
                      const val = responses[q.question_id];
                      const answered = val !== null && val !== undefined && val !== "";
                      return (
                        <div
                          key={q.question_id}
                          className={`relative rounded-[10px] border bg-white p-5 transition-all hover:border-[#bfdbfe] ${
                            answered
                              ? "border-[#e5e7eb] border-l-[3px] border-l-[#10b981]"
                              : "border-[#e5e7eb]"
                          }`}
                          data-testid={`cdu-question-${q.question_id}`}
                        >
                          {answered && (
                            <Check className="absolute top-4 right-4 h-4 w-4 text-[#10b981]" strokeWidth={3} />
                          )}
                          <div className="flex items-start gap-3">
                            <span className="text-[11px] text-[#9ca3af] font-mono font-semibold bg-[#f3f4f6] px-2 py-0.5 rounded shrink-0 mt-0.5">{q.question_id}</span>
                            <div className="flex-1 min-w-0 pr-6">
                              <p className="text-[14px] text-[#111827] font-medium leading-[1.5] mb-3">{q.question_text}</p>
                              {q.response_type === "standard_0_to_4_quality_scale" && (
                                <ScaleInput
                                  value={responses[q.question_id]}
                                  onChange={v => setResponses(p => ({ ...p, [q.question_id]: v }))}
                                  qid={q.question_id}
                                />
                              )}
                              {q.response_type === "yes_no_scale" && (
                                <div className="flex gap-2">
                                  {[{v:1,l:"Yes"},{v:0,l:"No"}].map(o => (
                                    <button
                                      key={o.v}
                                      onClick={() => setResponses(p => ({ ...p, [q.question_id]: o.v }))}
                                      className={`h-10 px-5 rounded-[8px] text-[13px] font-semibold border transition-all ${responses[q.question_id] === o.v
                                        ? "bg-[#1a56db] text-white border-[#1a56db] shadow-[0_0_0_3px_rgba(26,86,219,0.12)]"
                                        : "bg-[#f9fafb] text-[#4b5563] border-[#e5e7eb] hover:bg-[#eff6ff] hover:border-[#bfdbfe] hover:text-[#1a56db]"}`}
                                    >{o.l}</button>
                                  ))}
                                </div>
                              )}
                              {["percentage","number","integer"].includes(q.response_type) && (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={responses[q.question_id] ?? ""}
                                    onChange={e => setResponses(p => ({ ...p, [q.question_id]: e.target.value === "" ? null : Number(e.target.value) }))}
                                    placeholder={q.response_type === "percentage" ? "0 - 100" : "Enter value"}
                                    className="h-10 w-40 rounded-[8px] border border-[#e5e7eb] bg-white px-3 text-[14px] text-[#111827] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#1a56db] focus:ring-[3px] focus:ring-[#1a56db]/10"
                                  />
                                  {q.response_type === "percentage" && <span className="text-[14px] text-[#6b7280] font-medium">%</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Bottom actions */}
              <div className="pt-5 border-t border-[#e5e7eb] flex items-center justify-between gap-3 flex-wrap">
                <Button
                  onClick={() => handleSavePillar(currentPillar.key)}
                  disabled={saving}
                  className="h-10 rounded-[8px] bg-[#1a56db] text-white text-[13px] font-semibold hover:bg-[#143faa] px-5"
                  data-testid="cdu-save-continue-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" strokeWidth={3} />}Save Responses
                </Button>
                {currentIdx >= 0 && currentIdx < pillars.length - 1 && (
                  <Button
                    variant="outline"
                    onClick={() => { handleSavePillar(currentPillar.key); setActivePillar(pillars[currentIdx + 1].key); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="h-10 rounded-[8px] bg-white border border-[#e5e7eb] text-[#374151] text-[13px] font-semibold hover:bg-[#f9fafb] px-5"
                  >
                    Next Pillar <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-10 text-center">
              <p className="text-[14px] text-[#6b7280]">Select a pillar to begin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════ MAIN CDU COMPONENT ════ */
export default function CaporilliDiagnostic({ tenantId, externalTab = null, onTabChange = null, hideTabs = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [trend, setTrend] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [internalTab, setInternalTab] = useState("diagnostic");
  const tab = externalTab || internalTab;
  const setTab = (val) => {
    setInternalTab(val);
    if (onTabChange) onTabChange(val);
  };

  const load = useCallback(async () => {
    try {
      const [st, snaps, tr, cyc] = await Promise.all([
        api(`tenants/${tenantId}/status`).catch(() => ({ status: "not_purchased" })),
        govApi(`tenants/${tenantId}/snapshots`).catch(() => ({ snapshots: [] })),
        govApi(`tenants/${tenantId}/trend`).catch(() => ({ quarterly_trend: [], intervention_reviews: [] })),
        govApi(`tenants/${tenantId}/cycles`).catch(() => ({ cycles: [] })),
      ]);
      setStatus(st);
      setSnapshots(snaps.snapshots || []);
      setTrend(tr);
      setCycles(cyc.cycles || []);
      const hasRes = st?.has_results || (snaps.snapshots || []).length > 0 || st?.status === "completed";
      if (hasRes && tab === "diagnostic") setTab("overview");
    } catch {} finally { setLoading(false); }
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-[#9ca3af]" />
    </div>
  );

  if (status?.status === "not_purchased") return <PaymentView tenantId={tenantId} onDone={load} />;

  const activeCycle = cycles.find(c => ["active", "in_progress"].includes(c.status));

  const TABS = [
    { key: "diagnostic", label: "Diagnostic", icon: Shield },
    { key: "overview", label: "Overview", icon: BarChart3 },
    { key: "history", label: "History", icon: History },
    { key: "whatif", label: "What-If", icon: FlaskConical },
    { key: "actions", label: "Action Plan", icon: ListTodo },
  ];

  return (
    <div className="space-y-5" data-testid="cdu-governance">
      {!hideTabs && (
        <div className="flex items-center gap-1 border-b border-[#e5e7eb]">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 flex items-center gap-1.5 transition-colors ${
                  active ? "border-[#1a56db] text-[#1a56db]" : "border-transparent text-[#6b7280] hover:text-[#111827]"
                }`}
                data-testid={`cdu-subtab-${t.key}`}
              >
                <Icon className="h-3.5 w-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      )}

      {tab === "diagnostic" && <DiagnosticTab tenantId={tenantId} status={status} cycles={cycles} refresh={load} />}
      {tab === "overview" && <OverviewTab tenantId={tenantId} cycles={cycles} snapshots={snapshots} activeCycle={activeCycle} refresh={load} />}
      {tab === "history" && <HistoryTab tenantId={tenantId} snapshots={snapshots} trend={trend} />}
      {tab === "whatif" && <WhatIfTab tenantId={tenantId} />}
      {tab === "actions" && <ActionPlanTab tenantId={tenantId} snapshots={snapshots} />}
    </div>
  );
}
